import { useState } from 'react';
import { Send, Star, CheckCircle, X, Edit3, RefreshCw } from 'lucide-react';
import { Poll, PollAnswer } from '../../services/polls';
import toast from 'react-hot-toast';

interface PollDisplayProps {
  poll: Poll;
  onSubmit: (answers: PollAnswer[]) => Promise<void>;
  onDismiss?: () => void;
  alreadyAnswered: boolean;
  overlay?: boolean;
  previousAnswers?: PollAnswer[];
  isUpdate?: boolean;
}

export default function PollDisplay({ poll, onSubmit, onDismiss, alreadyAnswered, overlay, previousAnswers, isUpdate }: PollDisplayProps) {
  const [answers, setAnswers] = useState<Map<string, string | number>>(() => {
    if (previousAnswers && previousAnswers.length > 0) {
      const map = new Map<string, string | number>();
      previousAnswers.forEach(a => map.set(a.questionId, a.value));
      return map;
    }
    return new Map();
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(alreadyAnswered);

  const setAnswer = (questionId: string, value: string | number) => {
    setAnswers(prev => {
      const next = new Map(prev);
      next.set(questionId, value);
      return next;
    });
  };

  const handleSubmit = async () => {
    // Validate required questions
    for (const q of poll.questions) {
      if (q.required && !answers.has(q.id)) {
        toast.error(`Please answer: "${q.text}"`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const pollAnswers: PollAnswer[] = [];
      for (const [questionId, value] of answers) {
        pollAnswers.push({ questionId, value });
      }
      await onSubmit(pollAnswers);
      setSubmitted(true);
      toast.success('Response submitted!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && !poll.editable) {
    return (
      <div className="card p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <div>
            <p className="font-medium text-green-700 dark:text-green-400">{poll.title}</p>
            <p className="text-sm text-green-600 dark:text-green-500">Answered — thank you!</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted && poll.editable) {
    return (
      <div className="card p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div>
              <p className="font-medium text-green-700 dark:text-green-400">{poll.title}</p>
              <p className="text-sm text-green-600 dark:text-green-500">Answered — thank you!</p>
            </div>
          </div>
          <button
            onClick={() => setSubmitted(false)}
            className="btn btn-secondary btn-sm flex items-center gap-1 text-xs"
          >
            <Edit3 className="w-3 h-3" /> Edit Response
          </button>
        </div>
      </div>
    );
  }

  const content = (
    <div className={overlay ? 'space-y-4' : 'card p-4 border-primary/30 space-y-4'}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white">{poll.title}</h3>
        {onDismiss && !poll.mandatory && (
          <button
            onClick={onDismiss}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
            title="Skip this poll"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      {poll.mandatory && (
        <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">This poll is required to continue</p>
      )}

      {poll.questions.map((q) => (
        <div key={q.id} className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {q.text}
            {q.required && <span className="text-red-500 ml-0.5">*</span>}
          </p>

          {/* Multiple Choice */}
          {q.type === 'multiple-choice' && (
            <div className="space-y-1.5">
              {(q.options || []).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setAnswer(q.id, opt)}
                  className="flex items-center gap-2 w-full text-left group"
                >
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                    answers.get(q.id) === opt
                      ? 'border-primary bg-primary'
                      : 'border-gray-300 dark:border-gray-600 group-hover:border-primary/50'
                  }`}>
                    {answers.get(q.id) === opt && <span className="w-2 h-2 rounded-full bg-white" />}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{opt}</span>
                </button>
              ))}
            </div>
          )}

          {/* Yes / No */}
          {q.type === 'yes-no' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAnswer(q.id, 'yes')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  answers.get(q.id) === 'yes'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
              >
                Yes
              </button>
              <button
                onClick={() => setAnswer(q.id, 'no')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  answers.get(q.id) === 'no'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                }`}
              >
                No
              </button>
            </div>
          )}

          {/* Rating */}
          {q.type === 'rating' && (
            <div className="flex items-center gap-1">
              {Array.from({ length: q.ratingMax || 5 }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  onClick={() => setAnswer(q.id, n)}
                  className="p-0.5 transition-colors"
                >
                  <Star className={`w-6 h-6 ${
                    (answers.get(q.id) as number || 0) >= n
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300 dark:text-gray-600'
                  }`} />
                </button>
              ))}
              {answers.has(q.id) && (
                <span className="text-xs text-gray-500 ml-2">{answers.get(q.id)}/{q.ratingMax || 5}</span>
              )}
            </div>
          )}

          {/* Free Text */}
          {q.type === 'free-text' && (
            <textarea
              value={(answers.get(q.id) as string) || ''}
              onChange={e => setAnswer(q.id, e.target.value)}
              placeholder="Type your answer..."
              className="input w-full h-20 resize-none text-sm"
              maxLength={500}
            />
          )}
        </div>
      ))}

      {isUpdate && (
        <p className="text-xs text-blue-500 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> You are updating your previous response
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="btn btn-primary btn-sm flex-1 flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          {submitting ? 'Submitting...' : (isUpdate ? 'Update Response' : 'Submit Response')}
        </button>
        {onDismiss && !poll.mandatory && (
          <button
            onClick={onDismiss}
            className="btn btn-secondary btn-sm"
          >
            Skip
          </button>
        )}
      </div>
    </div>
  );

  return content;
}
